import { isSlidingDoor } from "./doorClearance";
import { resolveHostSegment, type WallHostRoom } from "./interiorWalls";
import type { Opening, Vec2, WallMount } from "./types";

/**
 * Top-down wall markers shared by the canvas and the My rooms thumbnail.
 * Both views place doors, windows, and mounts from the same along-wall
 * math so a saved layout can't disagree with the editor.
 */

function asHostRoom(roomOrPoints: Vec2[] | WallHostRoom): WallHostRoom {
  return Array.isArray(roomOrPoints) ? { points: roomOrPoints } : roomOrPoints;
}

export interface OpeningOnWall {
  id: string;
  kind: Opening["kind"];
  /** Hinged doors draw a swing. Windows, wall cutouts and sliders do not. */
  swings: boolean;
  /** A sliding door: its panels draw along the wall. */
  slides: boolean;
  width: number;
  start: Vec2;
  end: Vec2;
  /**
   * Unit normal pointing left of the wall direction. For a CCW room
   * outline that is into the room; the thumbnail and canvas both use it
   * for the door swing without enforcing winding.
   */
  nx: number;
  ny: number;
  /**
   * Length used to clamp the opening onto the edge. A degenerate edge
   * uses 1 so the division that produced the unit vector stays finite —
   * same fallback the canvas marker has always used.
   */
  len: number;
}

/** Segment of `opening` along its wall, in room inches. Null when the
 *  wall index doesn't exist. */
export function openingOnWall(
  opening: Opening,
  roomOrPoints: Vec2[] | WallHostRoom
): OpeningOnWall | null {
  const room = asHostRoom(roomOrPoints);
  const resolved = resolveHostSegment(room, opening);
  if (!resolved) return null;
  const len = resolved.len || 1;
  const ux = resolved.len === 0 ? 0 : (resolved.b.x - resolved.a.x) / resolved.len;
  const uy = resolved.len === 0 ? 0 : (resolved.b.y - resolved.a.y) / resolved.len;
  // `-0` stringifies differently from `0` and would show up in SVG attrs.
  const nx = -uy || 0;
  const ny = ux || 0;
  const halfW = opening.width / 2;
  const startAlong = Math.max(0, opening.alongWall - halfW);
  const endAlong = Math.min(len, opening.alongWall + halfW);
  const slides = isSlidingDoor(opening);
  return {
    id: opening.id,
    kind: opening.kind,
    swings: opening.kind === "door" && opening.style !== "cutout" && !slides,
    slides,
    width: opening.width,
    start: {
      x: resolved.a.x + ux * startAlong,
      y: resolved.a.y + uy * startAlong,
    },
    end: {
      x: resolved.a.x + ux * endAlong,
      y: resolved.a.y + uy * endAlong,
    },
    nx,
    ny,
    len,
  };
}

/** Fraction of a sliding door's width each of its two panels covers, so
 *  they overlap at the middle. */
export const SLIDER_PANEL_SHARE = 0.55;

export interface SlidingPanel {
  start: Vec2;
  end: Vec2;
  /** Which face of the wall the panel runs along: 1 = the normal's side. */
  side: 1 | -1;
}

/** The two bypass panels of a sliding door, one on each face of the track. */
export function slidingPanels(o: Pick<OpeningOnWall, "start" | "end">): SlidingPanel[] {
  const at = (t: number) => ({
    x: o.start.x + (o.end.x - o.start.x) * t,
    y: o.start.y + (o.end.y - o.start.y) * t,
  });
  return [
    { start: at(0), end: at(SLIDER_PANEL_SHARE), side: -1 },
    { start: at(1 - SLIDER_PANEL_SHARE), end: at(1), side: 1 },
  ];
}

export interface MountOnWall {
  id: string;
  color: string;
  start: Vec2;
  end: Vec2;
  len: number;
}

/** Tick of `mount` along its wall, in room inches. Not clamped to the
 *  edge — a mount that overhangs the corner still draws its full width,
 *  matching the canvas. Null when the wall is missing or degenerate. */
export function mountOnWall(
  mount: WallMount,
  roomOrPoints: Vec2[] | WallHostRoom
): MountOnWall | null {
  const resolved = resolveHostSegment(asHostRoom(roomOrPoints), mount);
  if (!resolved || resolved.len === 0) return null;
  const frame = {
    a: resolved.a,
    ux: (resolved.b.x - resolved.a.x) / resolved.len,
    uy: (resolved.b.y - resolved.a.y) / resolved.len,
    len: resolved.len,
  };
  const halfW = mount.width / 2;
  const { a, ux, uy, len } = frame;
  return {
    id: mount.id,
    color: mount.color,
    start: {
      x: a.x + ux * (mount.alongWall - halfW),
      y: a.y + uy * (mount.alongWall - halfW),
    },
    end: {
      x: a.x + ux * (mount.alongWall + halfW),
      y: a.y + uy * (mount.alongWall + halfW),
    },
    len,
  };
}
